import { createSlice } from '@reduxjs/toolkit';

/**
 * Supplementary data is now loaded lazily (on demand) rather than upfront on AOI draw.
 * Data arrives incrementally as users interact with features, so the store supports:
 * - Merging new entries without replacing existing ones
 * - Per-asset loading/error tracking for granular UI feedback
 */
const initialState = {
    data: {}, // Keyed by dataProviderAssignedUniqueId → { nuarActor, contacts, linkedFiles, guidances, ... }
    error: false, // Global error flag (legacy — used when the entire batch fails)
    loading: {}, // Per-asset loading state: { [assetId]: true }
    assetErrors: {}, // Per-asset error state: { [assetId]: errorMessage }
    featureIdList: [] // Full list of { dataProviderAssignedUniqueId, dataProviderId } from AOI features
};

/**
 * Parses the supplementary data API response into a keyed object.
 * Maps each asset's dataProviderId to its nuarActor for O(1) lookup,
 * then indexes entries by dataProviderAssignedUniqueId for store consumption.
 */
function parseSupplementaryPayload (suppDataList) {
    const nuarActorByDataProviderId = suppDataList?.nuarActors?.reduce((map, actor) => {
        map[actor.systemId] = actor;
        return map;
    }, {}) || {};

    const data = {};
    suppDataList?.supplementaryData?.forEach(({ dataProviderAssignedUniqueId, dataProviderId, ...suppData }) => {
        if (!dataProviderAssignedUniqueId) {
            return;
        }
        const nuarActor = nuarActorByDataProviderId[dataProviderId] || null;
        data[dataProviderAssignedUniqueId] = {
            dataProviderId,
            nuarActor,
            ...suppData
        };
    });
    return data;
}

export const supplementaryDataReducer = createSlice({
    name: 'supplementaryData',
    initialState: { ...initialState },
    reducers: {
        // Full replacement — used by the legacy retry path when all data is refetched at once
        setSupplementaryData: (state, action) => {
            const data = parseSupplementaryPayload(action?.payload);
            return {
                ...state,
                data,
                error: action?.payload === undefined
            };
        },
        // Incrementally merge new supplementary data into the store (lazy loading pattern)
        mergeSupplementaryData: (state, action) => {
            const newData = parseSupplementaryPayload(action?.payload);
            const loading = { ...state.loading };
            const assetErrors = { ...state.assetErrors };

            // Clear loading and errors for successfully fetched assets
            Object.keys(newData).forEach(id => {
                delete loading[id];
                delete assetErrors[id];
            });

            return {
                ...state,
                data: { ...state.data, ...newData },
                loading,
                assetErrors
            };
        },
        // Mark specific assets as loading (called before a lazy fetch starts)
        setSupplementaryDataLoading: (state, action) => {
            const assetIds = action?.payload || [];
            const loading = { ...state.loading };
            assetIds.forEach(id => {
                loading[id] = true;
            });
            return {
                ...state,
                loading
            };
        },
        // Set or clear per-asset errors. Pass error: null to clear (used before retry).
        setSupplementaryDataAssetError: (state, action) => {
            const { assetIds, error } = action?.payload || {};
            const loading = { ...state.loading };
            const assetErrors = { ...state.assetErrors };

            (assetIds || []).forEach(id => {
                delete loading[id];
                if (error) {
                    assetErrors[id] = error;
                } else {
                    delete assetErrors[id];
                }
            });

            return {
                ...state,
                loading,
                assetErrors
            };
        },
        // Store the full feature ID list from the AOI — used to build dataProviderList for lazy fetches
        setFeatureIdList: (state, action) => {
            return {
                ...state,
                featureIdList: action?.payload
            };
        },
        // Reset all data but preserve featureIdList (needed for subsequent lazy fetches)
        clearSupplementaryData: (state) => {
            return { ...initialState, featureIdList: state.featureIdList };
        },
        // Global error flag — sets error state and clears data (legacy path)
        setSupplementaryDataError: (state, action) => {
            return {
                ...state,
                data: {},
                error: action?.payload
            };
        }
    }
});

// Action creators are generated for each case reducer function
export const {
    setSupplementaryData,
    mergeSupplementaryData,
    setSupplementaryDataLoading,
    setSupplementaryDataAssetError,
    clearSupplementaryData,
    setSupplementaryDataError,
    setFeatureIdList
} = supplementaryDataReducer.actions;

export default supplementaryDataReducer.reducer;
